export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export async function fetchWithTimeout(
  url: RequestInfo | URL,
  options: RequestInit = {},
  timeout = 90000,
): Promise<Response> {
  const controller = new AbortController();
  const { signal } = options;

  if (signal?.aborted) {
    const reason = (signal as AbortSignal).reason;
    if (reason instanceof Error) {
      throw reason;
    }
    throw new Error(typeof reason === 'string' ? reason : 'The operation was aborted');
  }

  let abortListener: (() => void) | undefined;
  if (signal) {
    abortListener = () => controller.abort((signal as AbortSignal).reason);
    signal.addEventListener('abort', abortListener, { once: true });
  }

  const timer = setTimeout(() => {
    controller.abort(new TimeoutError(`Request timeout after ${timeout}ms`));
  }, timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    const reason = (controller.signal as AbortSignal).reason;
    if (reason instanceof TimeoutError) {
      throw reason;
    }
    if (reason instanceof Error) {
      throw reason;
    }
    throw error;
  } finally {
    clearTimeout(timer);
    if (abortListener && signal) {
      signal.removeEventListener('abort', abortListener);
    }
  }
}
